import {
	EarnProduct,
	EarnProductsByAsset,
	EarnAccordionOptions,
} from 'src/hooks/useEarnProduct';
import {
	ExploreSortKey,
	ExploreFilterCondition,
	ExploreFilterConfig,
} from './types';
import { OracleSource } from '@velocity-exchange/sdk';
import { ENUM_UTILS } from '@velocity-exchange/common';
import {
	EURC_MARKET_INDEX,
	SYRUP_USDC_MARKET_INDEX,
} from 'src/environmentVariables/EnvironmentVariables';
import { getSpotMarketConfig } from 'src/utils/api';

type SortableProduct = EarnProduct | EarnProductsByAsset | EarnAccordionOptions;

const SOL_AND_LST_MARKET_SYMBOLS = [
	'SOL',
	'mSOL',
	'jitoSOL',
	'bSOL',
	'dSOL',
	'BNSOL',
	'INF',
	'dfdvSOL',
];

export const isStableCoin = (marketIndex: number) => {
	const spotMarket = getSpotMarketConfig(marketIndex);
	if (!spotMarket) return false;

	return (
		ENUM_UTILS.match(spotMarket.oracleSource, OracleSource.PYTH_STABLE_COIN) ||
		ENUM_UTILS.match(
			spotMarket.oracleSource,
			OracleSource.PYTH_STABLE_COIN_PULL
		) ||
		ENUM_UTILS.match(
			spotMarket.oracleSource,
			OracleSource.PYTH_LAZER_STABLE_COIN
		) ||
		spotMarket.marketIndex === EURC_MARKET_INDEX ||
		spotMarket.marketIndex === SYRUP_USDC_MARKET_INDEX
	);
};

export const isSolAndLst = (marketSymbol: string) => {
	return SOL_AND_LST_MARKET_SYMBOLS.includes(marketSymbol);
};

export function sortExploreProducts<T extends SortableProduct>(
	products: T[],
	config: ExploreSortKey[]
): T[] {
	return [...products].sort((a, b) => {
		for (const sortKey of config) {
			const comparison = compareByKey(a, b, sortKey);
			if (comparison !== 0) {
				return comparison;
			}
		}
		return 0;
	});
}

function getMarketSymbol(product: SortableProduct): string {
	if ('marketSymbol' in product) {
		return product.marketSymbol;
	}
	if ('spotMarket' in product) {
		return product.spotMarket.symbol;
	}
	return '';
}

function getMarketIndex(product: SortableProduct): number | undefined {
	if ('marketIndex' in product) {
		return product.marketIndex;
	}
	if ('spotMarket' in product) {
		return product.spotMarket.marketIndex;
	}
	return undefined;
}

function getProductType(product: SortableProduct): string {
	if ('productType' in product) {
		return product.productType;
	}
	return '';
}

function compareByKey(
	a: SortableProduct,
	b: SortableProduct,
	sortKey: ExploreSortKey
): number {
	// Apply filter if present
	if ('filter' in sortKey && sortKey.filter) {
		const aPassesFilter = passesFilterConfig(a, sortKey.filter);
		const bPassesFilter = passesFilterConfig(b, sortKey.filter);

		// If only one passes the filter, prioritize it
		if (aPassesFilter && !bPassesFilter) return -1;
		if (!aPassesFilter && bPassesFilter) return 1;

		// If neither passes the filter, treat as equal
		if (!aPassesFilter && !bPassesFilter) return 0;
	}

	switch (sortKey.key) {
		// case 'apy': {
		// 	const apyA = a.apyToShow ?? 0;
		// 	const apyB = b.apyToShow ?? 0;
		// 	return sortKey.direction === 'desc' ? apyB - apyA : apyA - apyB;
		// }

		case 'market_symbol': {
			const symbolA = getMarketSymbol(a).toLowerCase();
			const symbolB = getMarketSymbol(b).toLowerCase();
			const comparison = symbolA.localeCompare(symbolB);
			return sortKey.direction === 'desc' ? -comparison : comparison;
		}

		case 'product_type': {
			const typeA = getProductType(a).toLowerCase();
			const typeB = getProductType(b).toLowerCase();
			const comparison = typeA.localeCompare(typeB);
			return sortKey.direction === 'desc' ? -comparison : comparison;
		}

		case 'stable_coin': {
			const marketIndexA = getMarketIndex(a);
			const marketIndexB = getMarketIndex(b);
			const aIsStable =
				marketIndexA !== undefined && isStableCoin(marketIndexA);
			const bIsStable =
				marketIndexB !== undefined && isStableCoin(marketIndexB);

			if (aIsStable && !bIsStable) return sortKey.direction === 'desc' ? -1 : 1;
			if (!aIsStable && bIsStable) return sortKey.direction === 'desc' ? 1 : -1;
			return 0;
		}

		case 'sol_and_lst': {
			const symbolA = getMarketSymbol(a);
			const symbolB = getMarketSymbol(b);
			const aIsSolOrLst = isSolAndLst(symbolA);
			const bIsSolOrLst = isSolAndLst(symbolB);

			if (aIsSolOrLst && !bIsSolOrLst)
				return sortKey.direction === 'desc' ? -1 : 1;
			if (!aIsSolOrLst && bIsSolOrLst)
				return sortKey.direction === 'desc' ? 1 : -1;
			return 0;
		}

		case 'specific_asset': {
			const targetSymbol = sortKey.symbol.toLowerCase();

			const symbolA = getMarketSymbol(a).toLowerCase();
			const symbolB = getMarketSymbol(b).toLowerCase();

			const aMatches = symbolA === targetSymbol;
			const bMatches = symbolB === targetSymbol;

			if (aMatches && !bMatches) return -1;
			if (!aMatches && bMatches) return 1;
			return 0;
		}

		default:
			return 0;
	}
}

function passesFilterConfig(
	product: SortableProduct,
	filterConfig: ExploreFilterConfig
): boolean {
	const { conditions, logic } = filterConfig;

	if (logic === 'and') {
		// All conditions must pass
		return conditions.every((condition) =>
			passesSingleCondition(product, condition)
		);
	} else {
		// At least one condition must pass
		return conditions.some((condition) =>
			passesSingleCondition(product, condition)
		);
	}
}

function passesSingleCondition(
	product: SortableProduct,
	condition: ExploreFilterCondition
): boolean {
	let value: number;

	switch (condition.field) {
		case 'apy':
			value = product.apyToShow ?? 0;
			break;
		case 'total_deposit':
			value = product.totalDepositQuote?.toNum() ?? 0;
			break;
	}

	switch (condition.operator) {
		case 'gte':
			return value >= condition.value;
		case 'lte':
			return value <= condition.value;
		case 'eq':
			return value === condition.value;
	}
}
